/**
 * Polyline intersection and spatial indexing.
 *
 * The compound layer of the geometry module: it builds on the segment atoms in
 * `./segments` and the AABB atoms in `./bounds` to report **every** crossing
 * between two polylines, each one tagged with the segment indices and
 * parametric positions a caller needs in order to split either polyline at the
 * crossing.
 *
 * Still domain-neutral — polylines are plain arrays of plain `{ x, y }` points,
 * and no input array or point object is ever mutated. `rbush` is the single
 * external dependency the whole module permits (project dependency at
 * `^4.0.1`, ES module with a default-exported class, imported here the same way
 * `src/globals/ToolSpatialIndex.js` does it). Nothing from Victor, Vue,
 * Vuetify, lodash or `src/features/` is imported.
 *
 * Conventions inherited from {@link intersectSegments} and documented on every
 * public entry point below: parametric positions are **inclusive** in `[0, 1]`
 * (so exact endpoint touches are reported), parallel and collinear segment
 * pairs yield no crossing at all, and the parallel test uses the absolute
 * `1e-10` epsilon from `./segments` (normalized-coordinate callers may need to
 * scale their inputs).
 *
 * @module lib/geom/polylines
 */

// `RBush`, `segmentBounds` and `boundsOverlap` back the two intersection
// strategies below; `polylineBounds` backs the cached AABB of `PolylineIndex`.
import RBush from 'rbush';
import { intersectSegments } from './segments';
import { segmentBounds, polylineBounds, boundsOverlap } from './bounds';

/**
 * Point shape accepted and produced by this module.
 *
 * @typedef {{ x: number, y: number }} Point
 */
export {};

/**
 * AABB shape produced by `./bounds`.
 *
 * @typedef {{ minX: number, minY: number, maxX: number, maxY: number }} Bounds
 */

/**
 * A single crossing between a segment of polyline `A` and a segment of
 * polyline `B`.
 *
 * `iA` / `iB` are zero-based **segment** indices (not vertex indices): segment
 * `i` of a polyline `P` runs `P[i] → P[i + 1]`, and the wrap segment of a
 * closed `n`-point polyline carries index `n - 1` and runs `P[n - 1] → P[0]`.
 * `tA` / `tB` locate the crossing along those segments, inclusive in `[0, 1]`,
 * so `point` can be reconstructed from either side as `P[i] + t * (P[i+1] - P[i])`.
 *
 * @typedef {Object} Crossing
 * @property {Point} point the crossing point, freshly allocated per entry
 * @property {number} iA segment index in polyline `A`
 * @property {number} iB segment index in polyline `B`
 * @property {number} tA parametric position along segment `iA` of `A`, in `[0, 1]`
 * @property {number} tB parametric position along segment `iB` of `B`, in `[0, 1]`
 */
export {};

/**
 * Number of segments a polyline contributes.
 *
 * `n - 1` for an open polyline, `n` for a closed one (the extra one being the
 * wrap segment `P[n - 1] → P[0]`), and `0` whenever the polyline has fewer than
 * 2 points — including non-array input. A `0` result is the short-circuit
 * signal for the degenerate case: no segments means no possible crossing.
 *
 * @param {Array<Point>} points polyline vertices
 * @param {boolean} [closed=false] whether the wrap segment is part of the polyline
 * @returns {number} segment count, `0` for degenerate input
 */
function countSegments(points, closed) {
    const n = Array.isArray(points) ? points.length : 0;
    if (n < 2) {
        return 0;
    }
    return closed ? n : n - 1;
}

/**
 * End point of segment `i` of `points`.
 *
 * Segment `i` runs `points[i] → points[i + 1]`, except for the wrap segment of
 * a closed polyline (`i === n - 1`), which runs `points[n - 1] → points[0]`.
 * This is the single place the wrap-index rule is encoded, so every enumeration
 * path agrees on it. The caller is responsible for keeping `i` below
 * {@link countSegments}; an open polyline simply never reaches the wrap index.
 *
 * @param {Array<Point>} points polyline vertices
 * @param {number} i zero-based segment index
 * @returns {Point} the segment's end point (never mutated, returned by reference)
 */
function segmentEnd(points, i) {
    const next = i + 1;
    return next < points.length ? points[next] : points[0];
}

/**
 * Enumerates a polyline's segments as `(index, p0, p1)` triples, in index
 * order.
 *
 * Visits `0 .. n - 2` for an open polyline and additionally the wrap segment
 * `(n - 1, points[n - 1], points[0])` when `closed` is `true` and `n >= 2`.
 * Nothing is visited when the polyline has fewer than 2 points, so callers can
 * rely on "no callback fired" meaning "no segments".
 *
 * Callback-based rather than array-returning so that hot loops allocate
 * nothing per segment; the points handed to `visit` are the caller's own
 * objects and must not be modified.
 *
 * @param {Array<Point>} points polyline vertices
 * @param {boolean} closed whether to include the wrap segment
 * @param {(i: number, p0: Point, p1: Point) => void} visit called once per segment
 * @returns {void}
 */
function forEachSegment(points, closed, visit) {
    const count = countSegments(points, closed);
    for (let i = 0; i < count; i++) {
        visit(i, points[i], segmentEnd(points, i));
    }
}

/**
 * Assembles a {@link Crossing} entry.
 *
 * The point is **copied** rather than referenced, so callers are free to pass a
 * reused scratch object as `point` without the retained result aliasing it.
 *
 * @param {Point} point crossing point; copied into the entry
 * @param {number} iA segment index in polyline `A`
 * @param {number} iB segment index in polyline `B`
 * @param {number} tA parametric position along segment `iA`, in `[0, 1]`
 * @param {number} tB parametric position along segment `iB`, in `[0, 1]`
 * @returns {Crossing}
 */
function makeCrossing(point, iA, iB, tA, tB) {
    return { point: { x: point.x, y: point.y }, iA, iB, tA, tB };
}

/**
 * Tests one pair of segments and, when they cross, returns the assembled
 * {@link Crossing}.
 *
 * {@link intersectSegments} is the authoritative gate: it decides whether there
 * is a crossing and produces the point, so this routine inherits its contract
 * verbatim — inclusive `[0, 1]` parameters, `null` for parallel/collinear pairs
 * (absolute `1e-10` epsilon on the 2D cross product) and `null` for zero-length
 * segments. Results are therefore identical to calling `intersectSegments`
 * directly, which is what the strategy-parity and reconstruction properties
 * assert.
 *
 * `intersectSegments` returns only the point, so `tA` / `tB` are recomputed
 * here with the very same expressions it uses internally (`denom = d1x * d2y -
 * d1y * d2x`, `t = (wx * d2y - wy * d2x) / denom`, `u = (wx * d1y - wy * d1x) /
 * denom` with `w = b0 - a0`). Identical operations in identical order on
 * identical inputs give bit-identical doubles, so the reported parameters are
 * exactly the ones that passed the `[0, 1]` test — no clamping needed, and the
 * extra arithmetic only runs for pairs that actually cross, never for the
 * pruned majority.
 *
 * @param {Point} a0 start of the `A` segment
 * @param {Point} a1 end of the `A` segment
 * @param {Point} b0 start of the `B` segment
 * @param {Point} b1 end of the `B` segment
 * @param {number} iA segment index in polyline `A`
 * @param {number} iB segment index in polyline `B`
 * @param {Point} [scratch] optional scratch point handed to `intersectSegments`
 *   so the rejected-pair path allocates nothing; never retained in the result
 * @returns {Crossing|null} the crossing entry, or `null` when the segments do
 *   not cross
 */
function segmentCrossing(a0, a1, b0, b1, iA, iB, scratch) {
    const point = intersectSegments(a0, a1, b0, b1, scratch);
    if (point === null) {
        return null;
    }

    // Same direction vectors, denominator and w as `intersectSegments`.
    const d1x = a1.x - a0.x;
    const d1y = a1.y - a0.y;
    const d2x = b1.x - b0.x;
    const d2y = b1.y - b0.y;

    const denom = d1x * d2y - d1y * d2x;

    const wx = b0.x - a0.x;
    const wy = b0.y - a0.y;

    const tA = (wx * d2y - wy * d2x) / denom;
    const tB = (wx * d1y - wy * d1x) / denom;

    return makeCrossing(point, iA, iB, tA, tB);
}
/**
 * Number of segment pairs (`nA * nB`, the size of the sweep brute force would
 * perform) above which the `'auto'` strategy switches to the rbush index.
 *
 * The index costs `O(nB log nB)` to build plus `O(nA log nB)` to query, so it
 * only pays for itself once the `O(nA * nB)` pair sweep gets large. `4096`
 * pairs — roughly a 64-segment polyline against another — is a conservative
 * crossover: the small hand-built polylines of everyday calls stay below it and
 * skip the tree entirely, while the anchoring 300-point closed outline against
 * a 749-point open contour sits near 225 000 pairs, far above it.
 *
 * @type {number}
 */
const INDEX_STRATEGY_THRESHOLD = 4096;

/**
 * Orders crossings by `(iA, iB)`.
 *
 * That is exactly the order a brute-force sweep emits (A's segments in index
 * order, each against B's segments in index order), and it is a *total* order
 * on a result set because a given segment pair yields at most one crossing. The
 * index strategy sorts its output with this comparator so both strategies
 * return crossings in the same order and not merely as equal sets — callers
 * comparing results do not have to canonicalize anything.
 *
 * @param {Crossing} x
 * @param {Crossing} y
 * @returns {number} negative, zero or positive per the `Array#sort` contract
 */
function compareCrossings(x, y) {
    return x.iA - y.iA || x.iB - y.iB;
}

/**
 * Brute-force strategy: every segment of `A` against every segment of `B`.
 *
 * Each pair goes through an AABB pre-prune (`segmentBounds` +
 * `boundsOverlap`) before the intersection math runs. The pre-prune is pure
 * optimization and changes no result: boxes that are disjoint on either axis
 * cannot contain a shared point, so {@link intersectSegments} would have
 * returned `null` for exactly those pairs.
 *
 * Allocation-free except for the crossings it keeps: the two bounding boxes and
 * the intersection point are reused scratch objects, and {@link makeCrossing}
 * copies the point into each retained entry rather than referencing the
 * scratch.
 *
 * @param {Array<Point>} ptsA vertices of polyline `A`
 * @param {boolean} closedA whether `A` includes its wrap segment
 * @param {Array<Point>} ptsB vertices of polyline `B`
 * @param {boolean} closedB whether `B` includes its wrap segment
 * @returns {Array<Crossing>} crossings ordered by `(iA, iB)`
 */
function collectBruteCrossings(ptsA, closedA, ptsB, closedB) {
    const nB = countSegments(ptsB, closedB);
    const crossings = [];

    const scratch = { x: 0, y: 0 };
    const boxA = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    const boxB = { minX: 0, minY: 0, maxX: 0, maxY: 0 };

    forEachSegment(ptsA, closedA, (iA, a0, a1) => {
        segmentBounds(a0, a1, boxA);

        for (let iB = 0; iB < nB; iB++) {
            const b0 = ptsB[iB];
            const b1 = segmentEnd(ptsB, iB);

            segmentBounds(b0, b1, boxB);
            if (!boundsOverlap(boxA, boxB)) {
                continue;
            }

            const crossing = segmentCrossing(a0, a1, b0, b1, iA, iB, scratch);
            if (crossing !== null) {
                crossings.push(crossing);
            }
        }
    });

    return crossings;
}

/**
 * Builds an rbush tree over a polyline's segment bounding boxes.
 *
 * Entries are `{ minX, minY, maxX, maxY, i }` with `i` the segment index, so a
 * query hit maps straight back to `points[i] → segmentEnd(points, i)`. The
 * boxes are bulk-loaded in one `load` call, which builds a better-balanced tree
 * than repeated inserts.
 *
 * The tree stores indices only — never point references — so it stays valid as
 * long as the array it was built from is not structurally changed, and building
 * it mutates nothing.
 *
 * Shared with {@link PolylineIndex}, whose constructor calls this directly.
 *
 * @param {Array<Point>} points polyline vertices
 * @param {boolean} closed whether to index the wrap segment too
 * @returns {RBush|null} the loaded tree, or `null` when the polyline has no
 *   segments (fewer than 2 points), in which case there is nothing to index
 */
function buildSegmentTree(points, closed) {
    const count = countSegments(points, closed);
    if (count === 0) {
        return null;
    }

    const entries = new Array(count);
    forEachSegment(points, closed, (i, p0, p1) => {
        // Declare all five fields up front, then fill the box through the `out`
        // pattern, so every entry shares one object shape.
        const entry = { minX: 0, minY: 0, maxX: 0, maxY: 0, i };
        segmentBounds(p0, p1, entry);
        entries[i] = entry;
    });

    const tree = new RBush();
    tree.load(entries);
    return tree;
}

/**
 * Index strategy: queries a prebuilt segment tree once per segment of `A`.
 *
 * For each segment of `A` in index order, the tree yields the `B` segments
 * whose boxes overlap its box; only those candidates go through
 * {@link segmentCrossing}. Pairs the tree prunes have disjoint AABBs and could
 * not have crossed, so the crossing set is identical to the brute-force one
 * (Req 4.6); the final sort makes the *order* identical too, since rbush
 * returns candidates in tree order rather than by segment index.
 *
 * `tree` must have been built from `ptsB` with the same `closed` flag — its
 * `i` values are looked up in `ptsB` here. {@link readSegmentTree} enforces
 * that for the caller-supplied-index path.
 *
 * Shared with {@link PolylineIndex}: `intersectPolyline(otherPts, { closed })`
 * is `collectIndexedCrossings(otherPts, closed, indexedPoints, tree)`, and
 * `intersectSegment(p0, p1)` is the same call with `[p0, p1]` and `false`,
 * which is what makes `iA` index the *query* polyline and `iB` the *indexed*
 * one (Req 5.2, 5.3).
 *
 * @param {Array<Point>} ptsA vertices of the querying polyline
 * @param {boolean} closedA whether `A` includes its wrap segment
 * @param {Array<Point>} ptsB vertices of the indexed polyline
 * @param {RBush} tree tree built by {@link buildSegmentTree} over `ptsB`
 * @returns {Array<Crossing>} crossings ordered by `(iA, iB)`
 */
function collectIndexedCrossings(ptsA, closedA, ptsB, tree) {
    const crossings = [];

    const scratch = { x: 0, y: 0 };
    const queryBox = { minX: 0, minY: 0, maxX: 0, maxY: 0 };

    forEachSegment(ptsA, closedA, (iA, a0, a1) => {
        segmentBounds(a0, a1, queryBox);

        const candidates = tree.search(queryBox);
        for (let c = 0; c < candidates.length; c++) {
            const iB = candidates[c].i;
            const crossing = segmentCrossing(
                a0,
                a1,
                ptsB[iB],
                segmentEnd(ptsB, iB),
                iA,
                iB,
                scratch
            );
            if (crossing !== null) {
                crossings.push(crossing);
            }
        }
    });

    if (crossings.length > 1) {
        crossings.sort(compareCrossings);
    }

    return crossings;
}

/**
 * Key under which a {@link PolylineIndex} publishes its segment tree for reuse
 * by {@link intersectPolylines}.
 *
 * A module-private `Symbol`, so the handshake stays internal to this file: the
 * rbush tree never shows up on the public surface of `PolylineIndex`, in
 * `Object.keys`, or in `JSON.stringify`.
 *
 * The {@link PolylineIndex} constructor sets `this[SEGMENT_TREE]` to a
 * {@link SegmentTreeHandle} describing the tree it built, or to `null` when it
 * was built from fewer than 2 points and therefore has no tree.
 * {@link readSegmentTree} is the only reader.
 *
 * @type {symbol}
 */
const SEGMENT_TREE = Symbol('lib/geom/polylines#segmentTree');

/**
 * What a {@link PolylineIndex} publishes under {@link SEGMENT_TREE}.
 *
 * `points`, `closed` and `segmentCount` are the build-time provenance of the
 * tree; {@link readSegmentTree} checks them before trusting the tree, because
 * the tree carries bare segment indices and reusing one built over a different
 * polyline would silently report crossings at the wrong coordinates.
 *
 * @typedef {Object} SegmentTreeHandle
 * @property {RBush} tree tree over the polyline's segment boxes
 * @property {Array<Point>} points the exact array the tree was built from
 * @property {boolean} closed the wrap-segment flag used at build time
 * @property {number} segmentCount `countSegments(points, closed)` at build time
 */

/**
 * Extracts a reusable segment tree from a caller-supplied `options.index`.
 *
 * Deliberately tolerant — a missing, foreign or stale index is not an error,
 * just a missed optimization, so every failed check returns `null` and
 * {@link intersectPolylines} falls back to building its own tree. A tree is
 * accepted only when it was built from the very same array (by reference) with
 * the same `closed` flag and the same segment count, which rules out both a
 * mismatched index and one whose array has since grown or shrunk. An index over
 * a deep-equal *copy* of `ptsB` is rejected on reference identity; results stay
 * correct, the call just rebuilds.
 *
 * @param {*} candidate whatever the caller passed as `options.index`
 * @param {Array<Point>} points the `B` polyline the tree must have been built from
 * @param {boolean} closed the wrap-segment flag the tree must have been built with
 * @param {number} segmentCount `countSegments(points, closed)` for the current call
 * @returns {RBush|null} the reusable tree, or `null` to build a fresh one
 */
function readSegmentTree(candidate, points, closed, segmentCount) {
    if (!candidate || typeof candidate !== 'object') {
        return null;
    }

    const handle = candidate[SEGMENT_TREE];
    if (!handle || typeof handle !== 'object') {
        return null;
    }

    if (handle.points !== points || handle.closed !== closed || handle.segmentCount !== segmentCount) {
        return null;
    }

    const tree = handle.tree;
    return tree && typeof tree.search === 'function' ? tree : null;
}

/**
 * Options accepted by {@link intersectPolylines}.
 *
 * @typedef {Object} IntersectPolylinesOptions
 * @property {boolean} [closedA=false] include `A`'s wrap segment `ptsA[n-1] → ptsA[0]`
 *   as segment index `n - 1`
 * @property {boolean} [closedB=false] same for `B`
 * @property {PolylineIndex} [index] a `PolylineIndex` built over `ptsB` with the
 *   matching `closed` flag; reused instead of building a tree, and ignored
 *   (without error) when it does not match
 * @property {'auto'|'brute'|'index'} [strategy='auto'] force a strategy instead
 *   of letting input size pick one; both produce the same crossings
 */

/**
 * Intersects two polylines and returns **every** crossing between them.
 *
 * Each entry is a {@link Crossing}: the point plus the segment indices and
 * parametric positions on both sides, which is what a caller needs to split
 * either polyline at the crossing. Segment `i` of a polyline `P` runs
 * `P[i] → P[i + 1]`; with the matching `closed` flag set, an `n`-point polyline
 * also contributes the wrap segment `P[n - 1] → P[0]` as index `n - 1`.
 * Crossings come back ordered by `(iA, iB)`.
 *
 * Conventions inherited from {@link intersectSegments}:
 * - **Inclusive `[0, 1]`**: `tA` and `tB` may be exactly `0` or `1`, so a
 *   crossing that lands on a shared vertex or on a segment endpoint is
 *   reported. A contour passing exactly through a vertex of `A` therefore shows
 *   up on both segments meeting at that vertex, as two entries.
 * - **Collinear yields nothing**: parallel and collinear segment pairs produce
 *   no crossing at all, so two polylines that run along each other over a
 *   stretch report only the crossings at genuinely transversal pairs.
 * - **Absolute `1e-10` epsilon** on the 2D cross product decides "parallel";
 *   it is expressed in input coordinate units, so callers working in
 *   normalized (`[0, 1]`) space may need to scale their points up before
 *   relying on that test.
 *
 * Degenerate input is answered with `[]`, never an exception: a polyline with
 * fewer than 2 points (or a non-array) has no segments and so no possible
 * crossing (Req 4.4), and two polylines that simply do not meet return the
 * same empty array (Req 4.5). Neither input array nor any point object is read
 * other than for its coordinates — nothing is mutated.
 *
 * Strategy: `'auto'` (the default) runs the brute-force sweep for small inputs
 * and switches to an rbush index over `B`'s segments once `nA * nB` exceeds
 * {@link INDEX_STRATEGY_THRESHOLD}; a usable `options.index` tips it to the
 * index path regardless of size, since the build cost is already paid.
 * `'brute'` and `'index'` force a path — mostly so tests can assert the two
 * agree (Req 4.6). The index only prunes pairs whose bounding boxes are
 * disjoint, which could not have crossed anyway, so the result is identical
 * either way.
 *
 * @param {Array<Point>} ptsA vertices of the first polyline
 * @param {Array<Point>} ptsB vertices of the second polyline
 * @param {IntersectPolylinesOptions} [options] closed flags, index reuse, strategy
 * @returns {Array<Crossing>} every crossing, ordered by `(iA, iB)`; `[]` when
 *   either polyline is degenerate or nothing crosses
 */
export function intersectPolylines(ptsA, ptsB, options) {
    const closedA = !!(options && options.closedA);
    const closedB = !!(options && options.closedB);

    const nA = countSegments(ptsA, closedA);
    const nB = countSegments(ptsB, closedB);

    // No segments on either side means no possible crossing (Req 4.4). This is
    // also what makes non-array input safe rather than throwing.
    if (nA === 0 || nB === 0) {
        return [];
    }

    const strategy = (options && options.strategy) || 'auto';
    if (strategy === 'brute') {
        return collectBruteCrossings(ptsA, closedA, ptsB, closedB);
    }

    const reused = readSegmentTree(options && options.index, ptsB, closedB, nB);

    // 'auto': brute force unless the sweep is big enough to pay for a tree, or
    // a reusable tree already exists. 'index' skips the size test entirely.
    if (strategy !== 'index' && reused === null && nA * nB <= INDEX_STRATEGY_THRESHOLD) {
        return collectBruteCrossings(ptsA, closedA, ptsB, closedB);
    }

    // `nB > 0`, so `buildSegmentTree` cannot return `null` here.
    const tree = reused !== null ? reused : buildSegmentTree(ptsB, closedB);
    return collectIndexedCrossings(ptsA, closedA, ptsB, tree);
}

/**
 * Key under which a {@link PolylineIndex} caches the AABB of its polyline.
 *
 * Module-private like {@link SEGMENT_TREE}, so the cache is reachable only
 * through the `bounds` getter and never shows up in `Object.keys` or
 * `JSON.stringify`.
 *
 * @type {symbol}
 */
const BOUNDS = Symbol('lib/geom/polylines#bounds');

/**
 * Options accepted by the {@link PolylineIndex} constructor and by
 * {@link PolylineIndex#intersectPolyline}.
 *
 * The object itself is optional everywhere it appears; omitting it (or passing
 * `undefined`) is the same as `{ closed: false }`.
 *
 * @typedef {Object} ClosedOption
 * @property {boolean} [closed=false] include the polyline's wrap segment
 *   `P[n-1] → P[0]` as segment index `n - 1`
 */

/**
 * A reusable rbush index over one polyline's segments.
 *
 * Built once, queried many times: the tree costs `O(n log n)` to build and
 * each query then costs `O(m log n)` instead of the `O(n · m)` sweep, which is
 * the whole point when a stable polyline (a tooth outline, a contour) is tested
 * against stroke after stroke. {@link intersectPolylines} accepts an instance as
 * `options.index` and reuses its tree, so the build cost is paid once even
 * across the two call styles.
 *
 * Queries report the standard {@link Crossing} shape with the roles fixed by
 * Req 5.2: **`iB` indexes this (the indexed) polyline** and **`iA` indexes the
 * polyline passed to the query**. `index.intersectPolyline(otherPts, { closed })`
 * is therefore equivalent to
 * `intersectPolylines(otherPts, index.points, { closedA: closed, closedB: index.closed, index })`,
 * crossing for crossing and in the same `(iA, iB)` order.
 *
 * All the conventions of {@link intersectPolylines} carry over, since the same
 * {@link intersectSegments} gate decides every pair: parametric positions are
 * **inclusive** in `[0, 1]` (endpoint and shared-vertex touches are reported),
 * parallel and collinear pairs yield no crossing, and "parallel" is decided by
 * an absolute `1e-10` epsilon on the 2D cross product — callers working in
 * normalized (`[0, 1]`) coordinates may need to scale their points up.
 *
 * Degenerate construction is answered with empty results rather than an
 * exception: from fewer than 2 points (or non-array input) there are no
 * segments, so no tree is built, `bounds` is `null` (Req 5.4) and both query
 * methods return `[]` (Req 5.5).
 *
 * `points` is stored **by reference** and never mutated (Req 6.3) — neither the
 * array nor its point objects — and the tree holds segment indices only, no
 * point references. The flip side is that the index goes stale if the array is
 * changed afterwards: structural changes are caught when
 * {@link intersectPolylines} validates a reused index (the segment count no
 * longer matches, so it quietly rebuilds), but in-place coordinate edits are
 * not detectable. Rebuild the index when the polyline changes.
 */
export class PolylineIndex {
    /**
     * Indexes `points`, caching its AABB and an rbush tree over its segment
     * boxes (the wrap segment included when `closed`, Req 5.1).
     *
     * Nothing is built when the polyline has fewer than 2 points — there are no
     * segments to index — which is the degenerate state the getter and both
     * query methods answer with `null` / `[]`.
     *
     * @param {Array<Point>} points polyline vertices; stored by reference and
     *   never mutated
     * @param {ClosedOption} [options] wrap-segment flag; omitting it means open
     */
    constructor(points, options) {
        const closed = !!(options && options.closed);
        const segmentCount = countSegments(points, closed);

        /**
         * The indexed polyline, by reference.
         *
         * @type {Array<Point>}
         */
        this.points = points;

        /**
         * Whether the wrap segment `points[n-1] → points[0]` is indexed.
         *
         * @type {boolean}
         */
        this.closed = closed;

        // `polylineBounds` happily boxes a single point, but Req 5.4 wants
        // `null` for anything built from fewer than 2 points, so the degenerate
        // case is decided by the segment count rather than by the box.
        this[BOUNDS] = segmentCount > 0 ? polylineBounds(points) : null;

        // Published for `intersectPolylines` to reuse; `readSegmentTree` checks
        // this provenance (same array by reference, same flag, same segment
        // count) before trusting the tree.
        this[SEGMENT_TREE] =
            segmentCount > 0
                ? { tree: buildSegmentTree(points, closed), points, closed, segmentCount }
                : null;
    }

    /**
     * AABB of the indexed polyline, computed once at construction.
     *
     * Equal to `polylineBounds(points)` for any polyline with at least 2
     * points, and `null` when the index was built from fewer than 2 points —
     * including non-array input (Req 5.4). The `closed` flag does not affect
     * it: the wrap segment adds no new vertices.
     *
     * The cached box is returned by reference, so treat it as read-only; copy it
     * before handing it to anything that writes through the `out` pattern.
     *
     * @returns {Bounds|null} `{ minX, minY, maxX, maxY }`, or `null` for a
     *   degenerate index
     */
    get bounds() {
        return this[BOUNDS];
    }

    /**
     * Every crossing between `otherPts` and the indexed polyline.
     *
     * `iA` / `tA` locate each crossing on `otherPts`, `iB` / `tB` on the indexed
     * polyline (Req 5.2); crossings come back ordered by `(iA, iB)`. Only the
     * query polyline's `closed` flag is taken here — the indexed polyline keeps
     * the flag it was built with.
     *
     * Conventions are those of {@link intersectPolylines}: inclusive `[0, 1]`
     * parameters, no crossing for parallel or collinear pairs, absolute `1e-10`
     * epsilon on the parallel test.
     *
     * Returns `[]` — never throws — when this index is degenerate (Req 5.5),
     * when `otherPts` has fewer than 2 points or is not an array, and when
     * nothing crosses.
     *
     * @param {Array<Point>} otherPts vertices of the querying polyline; not mutated
     * @param {ClosedOption} [options] wrap-segment flag for `otherPts`
     * @returns {Array<Crossing>} crossings ordered by `(iA, iB)`, `[]` when there are none
     */
    intersectPolyline(otherPts, options) {
        const handle = this[SEGMENT_TREE];
        if (handle === null) {
            return [];
        }

        const closed = !!(options && options.closed);
        if (countSegments(otherPts, closed) === 0) {
            return [];
        }

        return collectIndexedCrossings(otherPts, closed, this.points, handle.tree);
    }

    /**
     * Every crossing between the single segment `p0 → p1` and the indexed
     * polyline.
     *
     * The segment is treated as an open 2-point polyline, so this is exactly
     * `intersectPolyline([p0, p1])` and `iA` is always `0`; `iB` / `tB` locate
     * each crossing on the indexed polyline, `tA` locates it along `p0 → p1`
     * (Req 5.3). The endpoints are read, never mutated, and never retained.
     *
     * Same conventions as {@link PolylineIndex#intersectPolyline}: inclusive
     * `[0, 1]` parameters, nothing reported for a parallel or collinear pair,
     * and `[]` for a degenerate index (Req 5.5), a zero-length segment or no
     * crossing at all.
     *
     * @param {Point} p0 segment start
     * @param {Point} p1 segment end
     * @returns {Array<Crossing>} crossings ordered by `iB` (since `iA` is
     *   always `0`), `[]` when there are none
     */
    intersectSegment(p0, p1) {
        const handle = this[SEGMENT_TREE];
        if (handle === null) {
            return [];
        }

        return collectIndexedCrossings([p0, p1], false, this.points, handle.tree);
    }
}
